import { API_URLS } from '../config/api';
import { authToken } from './authToken';
import {
  LiveState,
  LiveStateResponse,
  TranscriptSegment,
  LiveInsight,
  SuggestedQuestion,
  RaisedQuestion,
  RaisedQuestionRemoval,
  TranscriberStatus,
  QuestionAction,
  TeamsTokenResponse,
  SpeechTokenResponse,
} from '../types/live';

// Live-meeting API client (contracts.md §8.2–§8.5). The Bearer header is added
// by the global fetch interceptor; SSE (EventSource) can't send headers, so the
// stream helper passes the JWT via ?access_token=.

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorText = await response.text();
    let message = `Request failed: ${response.status}`;
    try {
      const parsed = JSON.parse(errorText);
      message = parsed.detail || parsed.error || parsed.title || message;
    } catch {
      if (errorText) message = errorText;
    }
    const error = new Error(message) as Error & { status?: number };
    error.status = response.status;
    throw error;
  }
  if (response.status === 204) return undefined as T;
  return response.json();
}

const jsonHeaders = { 'Content-Type': 'application/json' };

export interface LiveStreamHandlers {
  onLiveState?: (state: LiveState) => void;
  onSlideChanged?: (slideRef: string, state?: LiveState) => void;
  onSegment?: (segment: TranscriptSegment) => void;
  onCleared?: () => void;
  onInsight?: (insight: LiveInsight) => void;
  onQuestion?: (question: SuggestedQuestion) => void;
  // Presenter-only: a participant raised a question to the presenter, or a
  // previously-raised question left the queue (addressed by the presenter or
  // lowered by its owner).
  onQuestionRaised?: (raised: RaisedQuestion) => void;
  onQuestionRemoved?: (removal: RaisedQuestionRemoval) => void;
  onHeartbeat?: () => void;
  onError?: (message: string) => void;
  onOpen?: () => void;
  onReconnecting?: (attempt: number, delayMs: number) => void;
}

// A live stream subscription with reconnect/backoff. Callers get a close()
// handle; the connection re-establishes automatically on error or heartbeat
// loss, retaining last-known UI state (graceful degradation, NFR).
export interface LiveStreamHandle {
  close: () => void;
}

const BACKOFF_BASE_MS = 1000;
const BACKOFF_MAX_MS = 15000;
const HEARTBEAT_TIMEOUT_MS = 45000;

class LiveAPIService {
  // Start the live session. materialFileIds (ordered) selects the presentation
  // deck (Unit 10a); omit/empty to present all materials. A 409 is thrown with
  // the server's message when the meeting is not in "prepared" status.
  async startLive(meetingId: string, materialFileIds?: string[]): Promise<LiveState> {
    const response = await fetch(API_URLS.getMeetingLiveStartUrl(meetingId), {
      method: 'POST',
      headers: jsonHeaders,
      body:
        materialFileIds && materialFileIds.length > 0
          ? JSON.stringify({ material_file_ids: materialFileIds })
          : undefined,
    });
    return (await handleResponse<LiveStateResponse>(response)).live_state;
  }

  async stopLive(meetingId: string): Promise<LiveState> {
    const response = await fetch(API_URLS.getMeetingLiveStopUrl(meetingId), {
      method: 'POST',
      headers: jsonHeaders,
    });
    return (await handleResponse<LiveStateResponse>(response)).live_state;
  }

  async setSlide(meetingId: string, slideRef: string): Promise<LiveState> {
    const response = await fetch(API_URLS.getMeetingLiveSlideUrl(meetingId), {
      method: 'PUT',
      headers: jsonHeaders,
      body: JSON.stringify({ slide_ref: slideRef }),
    });
    return (await handleResponse<LiveStateResponse>(response)).live_state;
  }

  async controlTranscriber(meetingId: string, action: 'start' | 'stop'): Promise<LiveState> {
    const response = await fetch(API_URLS.getMeetingLiveTranscriberUrl(meetingId), {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ action }),
    });
    return (await handleResponse<LiveStateResponse>(response)).live_state;
  }

  // Mint an ACS token for the Teams transcriber agent (controller-only; 409
  // when not the controller, 503 when ACS is not configured for the env).
  async getTeamsToken(meetingId: string): Promise<TeamsTokenResponse> {
    const response = await fetch(API_URLS.getMeetingLiveTeamsTokenUrl(meetingId), {
      method: 'POST',
      headers: jsonHeaders,
    });
    return handleResponse<TeamsTokenResponse>(response);
  }

  // Mint an Azure AI Speech authorization token for browser mic transcription
  // (controller-only; 409 when not the controller, 503 when Speech is not
  // configured — callers fall back to the browser Web Speech API on 503).
  async getSpeechToken(meetingId: string): Promise<SpeechTokenResponse> {
    const response = await fetch(API_URLS.getMeetingLiveSpeechTokenUrl(meetingId), {
      method: 'POST',
      headers: jsonHeaders,
    });
    return handleResponse<SpeechTokenResponse>(response);
  }

  // Post mic-captured (or pasted) segments. source defaults to "mic".
  async postSegments(
    meetingId: string,
    segments: { sequence: number; speaker?: string; text: string; timestamp: string }[],
    source: 'mic' | 'paste' | 'feed' = 'mic'
  ): Promise<{ accepted: number }> {
    const response = await fetch(API_URLS.getMeetingTranscriptSegmentsUrl(meetingId), {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ segments, source }),
    });
    return handleResponse<{ accepted: number }>(response);
  }

  // Clear all stored segments (controller-only). The server also broadcasts
  // transcript.cleared so every open view drops its transcript in step.
  async clearSegments(meetingId: string): Promise<{ cleared: number }> {
    const response = await fetch(API_URLS.getMeetingTranscriptSegmentsUrl(meetingId), {
      method: 'DELETE',
      headers: jsonHeaders,
    });
    return handleResponse<{ cleared: number }>(response);
  }

  // Per-user insights. scope "live" (default) or "prep". Returns [] on 404 so a
  // missing Unit 6 backend degrades gracefully rather than breaking the view.
  async getInsights(meetingId: string, scope: 'live' | 'prep' = 'live'): Promise<LiveInsight[]> {
    try {
      const response = await fetch(API_URLS.getMeetingInsightsUrl(meetingId, scope), {
        method: 'GET',
        headers: jsonHeaders,
      });
      if (response.status === 404) return [];
      const data = await handleResponse<{ insights: LiveInsight[] }>(response);
      return data.insights || [];
    } catch {
      return [];
    }
  }

  // Explicit user-requested refresh of the live insight/question batch. The
  // response carries the full post-regeneration set; new items also arrive over
  // SSE. A 409 (refresh already running / meeting not live) surfaces as an error
  // for the caller to toast.
  //
  // slideRef scopes the batch to a slide the caller is browsing independently of
  // the presenter (Unit 15c); omitted, the server uses the session's active
  // slide. The server validates the ref against the meeting's materials (400).
  async regenerateInsights(
    meetingId: string,
    slideRef?: string | null
  ): Promise<{ insights: LiveInsight[]; questions: SuggestedQuestion[] }> {
    const response = await fetch(API_URLS.getMeetingInsightsRegenerateUrl(meetingId), {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify(slideRef ? { slide_ref: slideRef } : {}),
    });
    const data = await handleResponse<{ insights: LiveInsight[]; questions: SuggestedQuestion[] }>(
      response
    );
    return { insights: data.insights || [], questions: data.questions || [] };
  }

  async getQuestions(meetingId: string): Promise<SuggestedQuestion[]> {
    try {
      const response = await fetch(API_URLS.getMeetingQuestionsUrl(meetingId), {
        method: 'GET',
        headers: jsonHeaders,
      });
      if (response.status === 404) return [];
      const data = await handleResponse<{ questions: SuggestedQuestion[] }>(response);
      return data.questions || [];
    } catch {
      return [];
    }
  }

  async actOnQuestion(
    meetingId: string,
    questionId: string,
    action: QuestionAction
  ): Promise<SuggestedQuestion> {
    const response = await fetch(API_URLS.getMeetingQuestionActUrl(meetingId, questionId), {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ action }),
    });
    const data = await handleResponse<{ question: SuggestedQuestion }>(response);
    return data.question;
  }

  // Subscribe to the live SSE stream with automatic reconnect + backoff and a
  // heartbeat watchdog. On connect the server sends live.state then replays
  // stored segments for catch-up (handled transparently by the handlers).
  openStream(meetingId: string, handlers: LiveStreamHandlers): LiveStreamHandle {
    let source: EventSource | null = null;
    let closed = false;
    let attempt = 0;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let heartbeatTimer: ReturnType<typeof setTimeout> | null = null;

    const clearTimers = () => {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (heartbeatTimer) clearTimeout(heartbeatTimer);
      reconnectTimer = null;
      heartbeatTimer = null;
    };

    const armHeartbeatWatchdog = () => {
      if (heartbeatTimer) clearTimeout(heartbeatTimer);
      heartbeatTimer = setTimeout(() => {
        // No heartbeat within the window: force a reconnect (retain last state).
        if (!closed) reconnect();
      }, HEARTBEAT_TIMEOUT_MS);
    };

    const parse = <T,>(raw: string): T | null => {
      try {
        return JSON.parse(raw) as T;
      } catch {
        return null;
      }
    };

    const reconnect = () => {
      if (closed) return;
      if (source) {
        source.close();
        source = null;
      }
      clearTimers();
      attempt += 1;
      const delay = Math.min(BACKOFF_BASE_MS * 2 ** (attempt - 1), BACKOFF_MAX_MS);
      handlers.onReconnecting?.(attempt, delay);
      reconnectTimer = setTimeout(connect, delay);
    };

    const connect = () => {
      if (closed) return;
      const token = authToken.get();
      if (!token) {
        handlers.onError?.('Not authenticated');
        return;
      }
      source = new EventSource(API_URLS.getMeetingLiveStreamUrl(meetingId, token));

      source.onopen = () => {
        attempt = 0;
        handlers.onOpen?.();
        armHeartbeatWatchdog();
      };

      source.addEventListener('live.state', (e) => {
        armHeartbeatWatchdog();
        const state = parse<LiveState>((e as MessageEvent).data);
        if (state) handlers.onLiveState?.(state);
      });

      source.addEventListener('slide.changed', (e) => {
        armHeartbeatWatchdog();
        const payload = parse<{ slide_ref?: string; active_slide_ref?: string; live_state?: LiveState }>(
          (e as MessageEvent).data
        );
        const ref = payload?.slide_ref || payload?.active_slide_ref || payload?.live_state?.active_slide_ref;
        if (ref) handlers.onSlideChanged?.(ref, payload?.live_state);
      });

      source.addEventListener('transcript.segment', (e) => {
        armHeartbeatWatchdog();
        const seg = parse<TranscriptSegment>((e as MessageEvent).data);
        if (seg) handlers.onSegment?.(seg);
      });

      source.addEventListener('transcript.cleared', () => {
        armHeartbeatWatchdog();
        handlers.onCleared?.();
      });

      source.addEventListener('insight.updated', (e) => {
        armHeartbeatWatchdog();
        const insight = parse<LiveInsight>((e as MessageEvent).data);
        if (insight) handlers.onInsight?.(insight);
      });

      source.addEventListener('question.updated', (e) => {
        armHeartbeatWatchdog();
        const q = parse<SuggestedQuestion>((e as MessageEvent).data);
        if (q) handlers.onQuestion?.(q);
      });

      source.addEventListener('question.raised', (e) => {
        armHeartbeatWatchdog();
        const raised = parse<RaisedQuestion>((e as MessageEvent).data);
        if (raised) handlers.onQuestionRaised?.(raised);
      });

      // Addressed (by presenter) and lowered (by owner) both remove the entry
      // from the presenter's raised-hands queue; the payload shape is identical.
      const onRemoved = (e: Event) => {
        armHeartbeatWatchdog();
        const removal = parse<RaisedQuestionRemoval>((e as MessageEvent).data);
        if (removal) handlers.onQuestionRemoved?.(removal);
      };
      source.addEventListener('question.addressed', onRemoved);
      source.addEventListener('question.lowered', onRemoved);

      source.addEventListener('heartbeat', () => {
        armHeartbeatWatchdog();
        handlers.onHeartbeat?.();
      });

      source.addEventListener('error', (e) => {
        const payload = parse<{ error?: string }>((e as MessageEvent).data);
        if (payload?.error) handlers.onError?.(payload.error);
      });

      // Transport-level error (connection drop): reconnect with backoff.
      source.onerror = () => {
        if (closed) return;
        reconnect();
      };
    };

    connect();

    return {
      close: () => {
        closed = true;
        clearTimers();
        if (source) {
          source.close();
          source = null;
        }
      },
    };
  }

  // Ask-AI streaming (contracts.md §8.5): POST that streams event/data frames in
  // its response body. Parsed via fetch + ReadableStream. Falls back to the
  // synchronous /chat endpoint when the stream endpoint is absent (404).
  async streamChat(
    meetingId: string,
    query: string,
    onDelta: (delta: string) => void,
    signal?: AbortSignal
  ): Promise<{ answer: string; status: string }> {
    const response = await fetch(API_URLS.getMeetingChatStreamUrl(meetingId), {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ query }),
      signal,
    });

    if (response.status === 404 || !response.body) {
      // Fallback: synchronous chat endpoint (Unit 6 lands the stream later).
      const sync = await fetch(API_URLS.getMeetingChatUrl(meetingId), {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({ query }),
        signal,
      });
      const data = await handleResponse<{ answer: string; status: string }>(sync);
      onDelta(data.answer);
      return { answer: data.answer, status: data.status };
    }

    if (!response.ok) {
      await handleResponse<unknown>(response);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let answer = '';
    let status = 'completed';

    const dispatchFrame = (frame: string) => {
      let eventName = 'message';
      const dataLines: string[] = [];
      for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) eventName = line.slice(6).trim();
        else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
      }
      if (dataLines.length === 0) return;
      const dataRaw = dataLines.join('\n');
      let data: any = null;
      try {
        data = JSON.parse(dataRaw);
      } catch {
        data = null;
      }
      if (eventName === 'chunk' && data?.delta) {
        answer += data.delta;
        onDelta(data.delta);
      } else if (eventName === 'done' && data) {
        if (typeof data.answer === 'string') answer = data.answer;
        if (typeof data.status === 'string') status = data.status;
      } else if (eventName === 'error') {
        throw new Error(data?.error || 'chat stream error');
      }
    };

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let sepIdx: number;
      // Frames are separated by a blank line (\n\n).
      while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, sepIdx);
        buffer = buffer.slice(sepIdx + 2);
        if (frame.trim()) dispatchFrame(frame);
      }
    }
    if (buffer.trim()) dispatchFrame(buffer);

    return { answer, status };
  }

  transcriberNextAction(status: TranscriberStatus): 'start' | 'stop' {
    return status === 'running' ? 'stop' : 'start';
  }
}

export const liveAPI = new LiveAPIService();
export default liveAPI;
